import { useEffect, useState } from "react";
import { renderTextWithLinks } from "../../../utils/function";
import { PhoneMockupProps } from "./sms/types";



export const PhoneMockup: React.FC<PhoneMockupProps> = ({
  message,
  senderName = "Darwin Stores",
  template,
  vars,
  cta,
  mediaFile,
}) => {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [resolvedText, setResolvedText] = useState<string>("");

  const varValues = [
    vars?.customerName || "[First_Name]",
    vars?.saleName || "[2]",
    vars?.discount || "[3]",
    vars?.promoCode || "[4]",
    vars?.expiryDate || "[5]",
  ];

  const hasMedia = template?.type === "Media + Text";
  const displayMessage = message || resolvedText || "Preview will appear here";
  const displaySenderName = senderName || "Darwin Stores";

  // Handle template resolution
  useEffect(() => {
    if (template && template.preview) {
      const resolved = template.preview.replace(/{{([1-9])}}/g, (_, n) =>
        varValues[parseInt(n) - 1] || `[${n}]`
      );
      setResolvedText(resolved);
    } else if (message) {
      setResolvedText(message);
    } else {
      setResolvedText("");
    }
  }, [template, message, vars]);

  // Handle media file object URL creation and cleanup
  useEffect(() => {
    // Clean up previous URL
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }

    // Create new URL only if mediaFile exists and is valid
    if (mediaFile && mediaFile instanceof File && hasMedia) {
      try {
        const url = URL.createObjectURL(mediaFile);
        setPreviewUrl(url);
      } catch (error) {

        setPreviewUrl(null);
      }
    }

    // Cleanup function
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [mediaFile, hasMedia]);

  return (
    <div className="mx-auto w-full  max-w-[280px]">
      <div className="relative bg-[#E4E4E8] rounded-[2.5rem] p-3 shadow-md border border-gray-200">
        {/* Top bar */}
        <div className="bg-gray-200 rounded-[2rem] overflow-hidden">
          {/* Status bar */}
          <div className="h-6 bg-gray-200 flex items-center justify-center">
            <div className="w-16 h-3 bg-gray-300 rounded-full" />
          </div>

          {/* Header */}
          <div className="bg-[#E4E4E8] px-3 py-2 flex items-center gap-2 border-b border-gray-100">
            <button className="text-[#7B7B7B] font-semibold">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>

            <div className="flex-1 flex flex-col items-center">
              <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center mb-0.5">
                <svg width="19" height="19" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3.50319 16.7506C5.17996 18.0994 7.26675 18.8354 9.41826 18.8367C11.5697 18.845 13.6581 18.1089 15.3288 16.7531C15.7068 16.4483 16.0607 16.1152 16.3878 15.7564C17.6565 14.3602 18.477 12.6152 18.7428 10.7472C19.0086 8.87937 18.7074 6.97471 17.8784 5.28005C17.0493 3.58527 15.7308 2.17857 14.0931 1.24226C12.4555 0.305792 10.5746 -0.11722 8.69395 0.0279248C6.81318 0.173066 5.01931 0.879786 3.54468 2.05625C2.07016 3.23286 0.982767 4.82534 0.42347 6.62725C-0.135788 8.42915 -0.141213 10.3574 0.407834 12.1625C0.957029 13.9675 2.03537 15.5661 3.50315 16.7507L3.50319 16.7506ZM9.41826 3.28008C10.1962 3.28008 10.9422 3.58922 11.4923 4.13924C12.0423 4.6894 12.3514 5.43559 12.3514 6.21363C12.3514 6.99166 12.0423 7.73782 11.4923 8.28801C10.9422 8.83817 10.1962 9.14732 9.41826 9.14732C8.64037 9.14732 7.89436 8.83818 7.34427 8.28801C6.79421 7.73785 6.48527 6.99166 6.48527 6.21363C6.48527 5.43559 6.7942 4.68944 7.34427 4.13924C7.89432 3.58923 8.64037 3.28008 9.41826 3.28008ZM9.41841 10.7862C13.6571 10.7862 15.5261 12.3579 15.8696 14.4195C15.7391 14.5872 15.6033 14.7513 15.4592 14.9096C15.1754 15.2208 14.8684 15.5098 14.5406 15.7742C13.0883 16.943 11.28 17.5801 9.41601 17.5796C7.55189 17.5792 5.74389 16.9413 4.29219 15.7717C3.96572 15.5082 3.66013 15.2199 3.37808 14.9095C3.23385 14.7515 3.09809 14.5876 2.96772 14.4201C3.31056 12.3582 5.1798 10.7862 9.4189 10.7862L9.41841 10.7862Z" fill="#878795" />
                </svg>

              </div>
              <span className="text-[10px] text-gray-600 font-medium">{displaySenderName}</span>
            </div>

            <div className="w-6" />
          </div>

          {/* Messages area */}
          <div className="bg-white min-h-[300px] px-3 py-3 space-y-2">
            <div className="flex justify-start">
              <div className="bg-gray-100 rounded-2xl rounded-tl-sm px-3 py-2 max-w-full">
                {/* Media */}
                {hasMedia && previewUrl && (
                  <img
                    src={previewUrl}
                    alt="media"
                    className="w-full rounded-xl mb-2 object-cover max-h-[150px]"
                    onError={() => setPreviewUrl(null)}
                  />
                )}

                {hasMedia && !previewUrl && mediaFile && (
                  <div className="w-full h-28 bg-gray-200 rounded-xl mb-2 flex items-center justify-center">
                    <svg className="w-6 h-6 text-[#7B7B7B] font-semibold" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                )}

                <p className="text-[11px] text-gray-700 leading-relaxed whitespace-pre-wrap break-words">
                  {renderTextWithLinks(displayMessage || (message ? message : "Preview will appear here"))}
                </p>

                {cta?.label && (
                  <div className="mt-2 pt-2 border-t border-gray-300 flex items-center gap-1">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                    </svg>
                    <span className="text-[10px] font-medium text-emerald-600">{cta.label}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Input bar */}
          <div className="bg-white border-t border-gray-100 px-3 py-2 flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="16" />
                <line x1="8" y1="12" x2="16" y2="12" />
              </svg>
            </div>

            <div className="flex-1 h-5 bg-gray-100 rounded-full" />

            <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="white">
                <path d="M2 21l21-9L2 3v7l15 2-15 2v7z" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

